import { InfoCard } from '@/components/info-card.js';
import { Button } from '@/components/ui/button.js';
import { LoaderIcon } from '@/components/ui/loader-icon.js';
import { getCoachVendor, getMeCoach } from '@/lib/api-calls.js';
import { useQuery } from '@tanstack/react-query';
import CreditCardIcon from '@/components/assets/credit-card-icon.svg?react';
import { Dialog, DialogContent, DialogTrigger } from '@/components/ui/dialog.js';
import { CoachPaymentNotSynced, CoachPaymentSyncForm } from './coach-payment-not-synced.js';
import { useState } from 'react';
import { InfoKeyValue } from '@/components/info-key-value.js';
import {
  accountClassToLabel,
  CoachPaymentDepositInfoStatusToLabel,
  coachPaymentDepositInfoStatusSchema,
  getCoachACHPaymentOrNull,
  getCoachPaymentInternationalOrNull,
  paymentAccountType,
  paymentAccountTypeKind,
  resolveCoachDepositView,
} from '@airreading/common/data';
import { cn } from '@/lib/tailwind-utils.js';

import { PaymentAlert } from './payment-alert.js';
import { maskedValueClassName } from '@/lib/masked-value.js';

export const CoachPaymentForm = () => {
  const { data: profile } = useQuery(getMeCoach());
  const hasStoredVendor = Boolean(profile?.vendorId?.trim());
  const {
    data: connection,
    isLoading,
    isError,
  } = useQuery({
    ...getCoachVendor(),
    enabled: hasStoredVendor,
  });
  const view = resolveCoachDepositView({
    hasStoredVendor,
    isLoading,
    isError,
    connection,
  });
  const vendor = connection?.vendor;
  const isInternationalAccount = profile?.paymentAccountType === paymentAccountType.enum.international_wire;
  const depositStatus = connection?.depositInfoStatus ?? coachPaymentDepositInfoStatusSchema.enum.not_ready;
  const showBankDetails = view === 'ready' || view === 'update';

  const [isUpdateAccountFormOpen, setIsUpdateAccountFormOpen] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const achPayment = getCoachACHPaymentOrNull(vendor);
  const internationalPayment = getCoachPaymentInternationalOrNull(vendor);
  const statusValue = (
    <div
      className={cn('text-orange-600 flex items-center gap-2', {
        'text-lime-600': depositStatus === coachPaymentDepositInfoStatusSchema.enum.ready,
      })}
    >
      <span className="w-2 h-2 rounded-full bg-current block" aria-hidden="true" />
      <span>{CoachPaymentDepositInfoStatusToLabel[depositStatus]}</span>
    </div>
  );

  return (
    <>
      <InfoCard
        headerText={'Direct deposit info'}
        headerActions={
          showBankDetails && (
            <Button
              variant={'outline'}
              onClick={() => setIsUpdateAccountFormOpen(true)}
              data-testid="profile-payment-form-button-update"
            >
              Update Direct Deposit Info
            </Button>
          )
        }
      >
        {view === 'loading' ? (
          <div role="status" className="flex items-center gap-2">
            <LoaderIcon className="h-4 w-4" />
            <span>Checking direct deposit status.</span>
          </div>
        ) : view === 'unavailable' ? (
          <p role="status" data-testid="profile-payment-alert-verification-unavailable" className="text-orange-600">
            We could not verify your direct deposit with Brex. Your saved bank connection was not changed. Try again in
            a few minutes.
          </p>
        ) : showBankDetails ? (
          <section>
            <div className="mb-4">{profile ? <PaymentAlert {...profile} /> : null}</div>

            {isInternationalAccount ? (
              <>
                <div className="grid grid-cols-4 gap-8">
                  <InfoKeyValue label="Account holder name" value={vendor?.company_name} />
                  <InfoKeyValue
                    label="Swift / BIC code"
                    labelClassName={maskedValueClassName}
                    value={internationalPayment?.payment_accounts.details.masked_swift_code}
                  />
                  <InfoKeyValue
                    label="IBAN / Account Number"
                    labelClassName={maskedValueClassName}
                    value={internationalPayment?.payment_accounts.details.masked_iban}
                  />
                  <InfoKeyValue
                    label="Country"
                    value={internationalPayment?.payment_accounts.details.address.country}
                  />
                  <InfoKeyValue label="Status" value={statusValue} />
                  <InfoKeyValue label="City" value={internationalPayment?.payment_accounts.details.address.city} />
                  <InfoKeyValue
                    label="Address"
                    value={
                      (internationalPayment?.payment_accounts.details.address.line1 ?? '') +
                      ' ' +
                      (internationalPayment?.payment_accounts.details.address.line2 ?? '')
                    }
                  />
                  <InfoKeyValue
                    label="ZIP code"
                    value={internationalPayment?.payment_accounts.details.address.postal_code}
                  />
                </div>
                {isUpdateAccountFormOpen && (
                  <CoachPaymentSyncForm
                    type="update"
                    defaultValues={{
                      companyName: internationalPayment?.company_name,
                    }}
                    onClose={() => setIsUpdateAccountFormOpen(false)}
                  />
                )}
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-8">
                  <InfoKeyValue label="Account holder name" value={vendor?.company_name} />
                  <InfoKeyValue
                    label="Account type"
                    value={
                      achPayment?.payment_accounts.details.type === paymentAccountTypeKind.enum.ACH
                        ? achPayment?.payment_accounts.details.account_type
                        : ''
                    }
                  />
                  <InfoKeyValue
                    label="Routing number"
                    labelClassName={maskedValueClassName}
                    value={achPayment?.payment_accounts.details.masked_routing_number}
                  />
                  <InfoKeyValue label="Status" value={statusValue} />
                  <InfoKeyValue
                    label="Account class"
                    value={
                      achPayment?.payment_accounts.details.type === paymentAccountTypeKind.enum.ACH
                        ? achPayment?.payment_accounts.details.account_class &&
                          accountClassToLabel[achPayment?.payment_accounts.details.account_class]
                        : ''
                    }
                  />
                  <InfoKeyValue
                    label="Account number"
                    className="not-italic normal-case tracking-normal"
                    labelClassName={maskedValueClassName}
                    value={achPayment?.payment_accounts.details.masked_account_number}
                  />
                </div>
                {isUpdateAccountFormOpen && (
                  <CoachPaymentSyncForm
                    type="update"
                    onClose={() => setIsUpdateAccountFormOpen(false)}
                    defaultValues={{
                      type: achPayment?.payment_accounts.details.type,
                      companyName: vendor?.company_name,
                      accountClass: achPayment?.payment_accounts.details.account_class ?? undefined,
                      accountType: achPayment?.payment_accounts.details.account_type ?? undefined,
                    }}
                    currentAccount={{
                      routingNumber: achPayment?.payment_accounts.details.masked_routing_number,
                      accountNumber: achPayment?.payment_accounts.details.masked_account_number,
                    }}
                  />
                )}
              </>
            )}
          </section>
        ) : (
          <section className="grid place-items-center">
            <CreditCardIcon className="mb-4" />
            <p className="text-slate-400 mb-2 font-normal text-lg">Direct deposit not ready yet</p>
            <Dialog open={isOpen} onOpenChange={setIsOpen}>
              <DialogTrigger asChild>
                <Button variant={'outline'} className="font-bold" data-testid="profile-payment-form-button-add">
                  Add direct deposit info
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="profile-payment-modal-add">
                <CoachPaymentNotSynced
                  onClose={() => setIsOpen(false)}
                  onOpen={() => setIsUpdateAccountFormOpen(true)}
                />
              </DialogContent>
            </Dialog>
          </section>
        )}
      </InfoCard>
      <div className="pb-4">
        <p className="text-[#606060]">
          International Bank Account Notice: Starting January 1st, 2026, we no longer support adding or changing
          international bank accounts. To receive payments internationally, please set up a Wise account at{' '}
          <a href="wise.com" className="text-blue-500">
            wise.com
          </a>
          , then enter your Wise US routing and account numbers here as an ACH payment method.With Wise, you can convert
          your USD payments to your local currency and withdraw to your local bank—typically at better rates and lower
          fees than traditional international wires.
        </p>
      </div>
    </>
  );
};
